import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  makeStyles,
  TextField,
  Typography
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import useNotifier from "@saleor/hooks/useNotifier";
import { useMallUpdateOrderTimeframe } from "@saleor/malls/mutations";
import { DialogProps } from "@saleor/types";
import Moment from "moment";
import React from "react";
import { useIntl } from "react-intl";

const useModalStyles = makeStyles(
  theme => ({
    btnCancel: {
      "& span": {
        color: "#858C94"
      },
      backgroundColor: "#FFFFFF",
      borderColor: "#858C94",
      color: "#858C94",
      ...{
        "&:hover": {
          backgroundColor: "#FFFFFF"
        }
      }
    },
    btnYes: {
      "&:disabled": {
        backgroundColor: "#8195ff"
      },
      "&:hover": {
        backgroundColor: theme.palette.primary.main
      },
      backgroundColor: theme.palette.primary.main
    },
    customAlert: {
      backgroundColor: "#F4F8FF",
      borderLeft: "10px solid " + theme.palette.primary.main,
      borderRadius: "8px",
      color: theme.palette.primary.main,
      marginBottom: "10px !important",
      padding: "10px 16px",
      width: "100%"
    },
    modal: {
      minHeight: "280px",
      position: "relative"
    },
    modalBottom: {
      height: "40px",
      margin: "10px 0px",
      textAlign: "right"
    },
    modalCloseBtn: {
      position: "absolute",
      right: "15px",
      top: "10px"
    },
    modalContent: {
      "& div": {
        marginBottom: "5px"
      },
      height: "calc(100% - 80px)",
      width: "100%"
    },
    modalTitleText: {
      fontSize: "24px",
      fontWeight: 600
    },
    txtWhite: {
      color: "#ffffff !important"
    }
  }),
  { name: "MallDeliveryTimeDialog" }
);

export interface MallDeliveryTimeDialogProps extends DialogProps {
  handleConfirm: () => void;
  handleCancel: () => void;
  mallId: string;
  shippingMethodId: string;
  cutOffTime: string;
  defaultStartTime?: string;
  defaultEndTime?: string;
}

const MallDeliveryTimeDialog: React.FC<MallDeliveryTimeDialogProps> = props => {
  const {
    open,
    onClose,
    handleConfirm,
    handleCancel,
    mallId,
    shippingMethodId,
    cutOffTime,
    defaultStartTime,
    defaultEndTime
  } = props;

  const classes = useModalStyles(props);
  const notify = useNotifier();
  const intl = useIntl();

  const [delTimeStart, setDelTimeStart] = React.useState("");
  const [delTimeEnd, setDelTimeEnd] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setDelTimeStart(defaultStartTime || "");
      setDelTimeEnd(defaultEndTime || "");
    } else {
      setDelTimeStart("");
      setDelTimeEnd("");
    }
  }, [open]);

  const saveDelTime = () => {
    if (!delTimeStart || !delTimeEnd) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "Invalid input"
        })
      });
      return;
    }

    const dtFormat = "YYYYMMDDHHmmss";
    const dateTimeNow = Moment(new Date()).format(dtFormat);
    const dateTimeWithBuffer = Moment(new Date())
      .add(15, "minutes")
      .format(dtFormat);
    const dateToday = Moment(new Date()).format("YYYY-MM-DD");
    const dateStart = Moment(dateToday + " " + delTimeStart).format(dtFormat);
    const dateEnd = Moment(dateToday + " " + delTimeEnd).format(dtFormat);
    const dateCutOff = Moment(dateToday + " " + cutOffTime).format(dtFormat);

    if (dateStart >= dateEnd) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "Start Time must be earlier than the end time"
        })
      });
    } else if (dateEnd < dateCutOff) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage:
            "End time must be after or the same as the set cut-off time for Same Day Delivery."
        })
      });
    } else if (dateTimeNow < dateEnd && dateEnd < dateTimeWithBuffer) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage:
            "Closing/end time should be 15 minutes after or ahead of the current time."
        })
      });
    } else {
      updateMall({
        variables: {
          endTime: delTimeEnd,
          mallId,
          shippingMethodId,
          startTime: delTimeStart
        }
      });
    }
  };

  const handleError = errors => {
    const [error] = errors.graphQLErrors;
    notify({
      status: "error",
      text: error?.message
    });
  };

  const [updateMall] = useMallUpdateOrderTimeframe({
    onCompleted: data => {
      if (data?.updateMallOrderTimeframe?.errors?.length === 0) {
        handleConfirm();
      } else if (data?.updateMallOrderTimeframe?.errors?.length > 0) {
        data.updateMallOrderTimeframe.errors.forEach(error => {
          notify({
            status: "error",
            text: error.message
          });
        });
      }
    },
    onError: handleError
  });

  const formatTime = timeString => {
    if (!timeString) {
      return "";
    }

    const [hours, minutes] = timeString.split(":");
    return hours + ":" + minutes;
  };

  return (
    <Dialog onClose={onClose} open={open} maxWidth="xs" fullWidth>
      <DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => onClose()}
          className={classes.modalCloseBtn}
        >
          <CloseIcon />
        </IconButton>
        <Typography className={classes.modalTitleText}>
          Delivery Time Availability
        </Typography>
      </DialogTitle>

      <DialogContent className={classes.modal}>
        <div className={classes.modalContent}>
          <div className={classes.customAlert}>
            The set delivery time will take effect on the next orders placed.
          </div>

          <div>
            <TextField
              id="delTimeStart"
              name="delTimeStart"
              label={"Start Time"}
              value={formatTime(delTimeStart)}
              onChange={e => setDelTimeStart(e.target.value)}
              type="time"
              InputLabelProps={{
                shrink: true
              }}
              fullWidth
            />
            <TextField
              id="delTimeEnd"
              name="delTimeEnd"
              label={"End Time"}
              value={formatTime(delTimeEnd)}
              onChange={e => setDelTimeEnd(e.target.value)}
              type="time"
              InputLabelProps={{
                shrink: true
              }}
              fullWidth
            />
          </div>
        </div>

        <div className={classes.modalBottom}>
          <Button
            id="cancelDialogBtn"
            autoFocus
            onClick={handleCancel}
            color="secondary"
            variant="outlined"
            style={{ marginRight: 10 }}
            className={classes.btnCancel}
          >
            Cancel
          </Button>
          <Button
            id="confirmDialogBtn"
            autoFocus
            onClick={saveDelTime}
            color="primary"
            variant="contained"
            className={classes.btnYes}
          >
            <span className={classes.txtWhite}>Save</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

MallDeliveryTimeDialog.displayName = "MallDeliveryTimeDialog";
export default MallDeliveryTimeDialog;
